/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import * as React from 'react';

// constants
import { ROLES } from '@config/constants/roles';
import { useWhoAmI } from 'api/get-whoami';

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */
type RoleTypes = keyof typeof ROLES;

/* -------------------------------------------------------------------------- */
/*                                    Lib                                     */
/* -------------------------------------------------------------------------- */

type RoleObject = {
	role: RoleTypes;
	ACLs: string[];
	organization?: string;
};

export function findPriorityRole(
	roles: RoleObject[]
): RoleObject | undefined {
	const priorities = [
		ROLES.SUPERADMIN,
		ROLES.ADMIN,
		ROLES.STAFF,
		ROLES.ORGANIZATION_ADMIN,
		ROLES.ORGANIZATION_STAFF,
		ROLES.ORGANIZATION_MEMBER,
	];

	const subPriorities: string[] = [
		ROLES.BRAND_ADMIN,
		ROLES.BRAND_STAFF,
		ROLES.PRODUCER_ADMIN,
		ROLES.PRODUCER_STAFF,
	];

	for (const priority of priorities) {
		const found = roles.find((r) => r.role === priority);
		if (found) {
			if (found.role === ROLES.ORGANIZATION_MEMBER) {
				const secondRole = roles.find((r) =>
					subPriorities.includes(r.role)
				);
				return secondRole;
			} else {
				return found;
			}
		}
	}
	return undefined;
}

function mergeACLs(data: RoleObject[]) {
	const aclSet = new Set();

	data.forEach((item) => {
		if (Array.isArray(item.ACLs)) {
			item.ACLs.forEach((acl) => aclSet.add(acl));
		}
	});

	return Array.from(aclSet).sort();
}

// Hook export checkAccess function & Role variable
export const useAuthorization = () => {
	const { data, isFetched } = useWhoAmI();

	const checkAccess = React.useCallback(
		({
			allowedRoles,
			allowedAcl,
		}: {
			allowedRoles: RoleTypes[];
			allowedAcl?: string;
		}) => {
			if (!isFetched || !data) {
				return false;
			}
			if (
				allowedRoles &&
				allowedRoles.length > 0 &&
				data
			) {
				let highestPriorityRole = null;

				if (data?.roles && Array.isArray(data?.roles)) {
					highestPriorityRole = findPriorityRole(
						data.roles
					);
				}

				const roleUser: RoleTypes =
					highestPriorityRole?.role
						? highestPriorityRole?.role
						: ROLES.USER;

				if (allowedAcl) {
					return (
						allowedRoles?.includes(data.role) &&
						data?.acls.includes(allowedAcl)
					);
				}

				return allowedRoles?.includes(roleUser);
			}

			return true;
		},
		[data, isFetched]
	);

	const checkRole = (allowedRoles: string[]) => {
		let highestPriorityRole = null;

		if (data?.roles && Array.isArray(data?.roles)) {
			highestPriorityRole = findPriorityRole(data.roles);
		}

		const roleUser: RoleTypes = highestPriorityRole?.role
			? highestPriorityRole.role
			: ROLES.USER;

		if (allowedRoles && data?.role) {
			return allowedRoles.includes(roleUser);
		}

		return false;
	};

	const checkAcl = React.useCallback(
		(allowedAcl: string) => {
			if (allowedAcl && Array.isArray(data?.roles)) {
				// Merge all ACLs
				const allAcls = mergeACLs(data?.roles);

				return allAcls.includes(allowedAcl);
			}

			return true;
		},
		[data, isFetched]
	);

	let userRole = null;
	userRole = data?.roles
		? findPriorityRole(data?.roles)
		: null;

	const primaryRole =
		data && Array.isArray(data?.roles)
			? data.roles.find(
					(role) => role.role === userRole?.role
				)
			: null;

	return {
		checkAcl,
		checkAccess,
		checkRole,
		role:
			isFetched && primaryRole?.role
				? primaryRole?.role
				: ROLES.USER,
		user: data,
		entity: primaryRole?.entity
			? primaryRole?.entity
			: null,
	};
};

type AuthorizationProps = {
	forbiddenFallback?: React.ReactNode;
	children: React.ReactNode;
} & (
	| {
			allowedRoles: RoleTypes[];
			policyCheck?: never;
	  }
	| {
			allowedRoles?: never;
			policyCheck: boolean;
	  }
);

export const Authorization = ({
	policyCheck,
	allowedRoles,
	forbiddenFallback = null,
	children,
}: AuthorizationProps) => {
	useAuthorization();

	const { checkAccess } = useAuthorization();

	let canAccess = false;

	if (allowedRoles) {
		canAccess = checkAccess({ allowedRoles });
	}

	if (typeof policyCheck !== 'undefined') {
		canAccess = policyCheck;
	}

	return <>{canAccess ? children : forbiddenFallback}</>;
};
